import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, distinctUntilChanged, map, tap } from "rxjs/operators";

import { AuthService } from "./auth.service";
import { SharedDataService } from "./shared-data.service";

/**
 * The 8 onboarding rows tracked toward the "Onboarding" completion percentage
 * (issue #523 / flymsg-be#759). FlyLearning is a real checklist row too, but it
 * is plan-gated and intentionally NOT part of this list — it stays excluded
 * from the percentage, exactly like the product spec calls for.
 */
export type OnboardingStepKey =
  | "install_extension"
  | "about_you"
  | "add_flycut_signature"
  | "test_flycut"
  | "add_flyplate"
  | "create_persona"
  | "linkedin_ai_comment_generator"
  | "linkedin_social_post_generator";

export const ONBOARDING_TRACKED_STEPS: readonly OnboardingStepKey[] = [
  "install_extension",
  "about_you",
  "add_flycut_signature",
  "test_flycut",
  "add_flyplate",
  "create_persona",
  "linkedin_ai_comment_generator",
  "linkedin_social_post_generator",
];

/**
 * Issue #520: the two onboarding-modal steps the August 2026 meeting decided
 * are mandatory — install_extension (panel 0) and about_you (panel 1). The
 * modal's X and backdrop/ESC dismissal stay blocked until BOTH are done; see
 * `requiredComplete`/`requiredComplete$` below, the single source every close
 * guard reads.
 */
export const REQUIRED_ONBOARDING_STEPS: readonly OnboardingStepKey[] = ["install_extension", "about_you"];

/**
 * Issue #537: the header hat's glow keeps its existing `icon-box-active`
 * styling, but the condition driving it moves off the legacy
 * `onboardingv2_completed` flag onto the per-step model — glow while NOT
 * all of the first three welcome-modal panels are done. This is a strict
 * superset of REQUIRED_ONBOARDING_STEPS (adds add_flycut_signature), so
 * it's tracked as its own list rather than derived from it.
 */
export const FIRST_THREE_ONBOARDING_STEPS: readonly OnboardingStepKey[] = [
  "install_extension",
  "about_you",
  "add_flycut_signature",
];

/**
 * Issue #537: cap on how many times the header-hat re-entry tooltip (shown
 * on a load where the hard-refresh bypass left the first three steps
 * incomplete and the walk-me modal isn't open) will show before it stops
 * for good. Exported so the tooltip's gating and its Karma specs share one
 * number instead of a duplicated magic constant.
 */
export const REENTRY_TOOLTIP_MAX_DISMISSALS = 3;

/**
 * The welcome-modal's 8 checklist rows (panel indexes 0-7, see
 * `welcome-modal.component.html`'s `*ngSwitchCase`s and
 * `onboarding-checklist.component.ts`'s row list) — the canonical
 * panel-index -> step-key lookup WelcomeModalComponent uses to know which
 * key a given panel's "Try it out!"/video-complete/form-completion action
 * completes.
 *
 * Issue #519: `about_you` was inserted at index 1 (right after
 * `install_extension`), shifting every step after it down by one panel. This
 * array is index-aligned to the CURRENT panel switch only — it is
 * deliberately NOT reused for `deriveInitialCompleted()`'s legacy
 * `onboardingv2_step` rollout-compat fallback below, because that counter was
 * recorded against the PRE-#519 numbering (`about_you` didn't exist yet, so
 * no existing user's counter value can legitimately mean "about_you done").
 * See `LEGACY_ONBOARDINGV2_STEP_ORDER`.
 */
export const WELCOME_MODAL_STEP_ORDER: readonly OnboardingStepKey[] = [
  "install_extension",
  "about_you",
  "add_flycut_signature",
  "test_flycut",
  "add_flyplate",
  "create_persona",
  "linkedin_ai_comment_generator",
  "linkedin_social_post_generator",
];

/**
 * Key-based lookup into `WELCOME_MODAL_STEP_ORDER`, for every caller that
 * needs to reopen the walk-me modal at a specific panel (issue #542). A
 * hardcoded panel-index literal silently goes stale the next time a step is
 * inserted into (or removed from) the order — exactly what happened when
 * issue #519 inserted `about_you` at index 1 and every external reopen call
 * site kept its pre-#519 int, landing users back on the step they just
 * finished. Always derive the index from the key instead of writing a new
 * literal.
 */
export function modalStepIndex(key: OnboardingStepKey): number {
  return WELCOME_MODAL_STEP_ORDER.indexOf(key);
}

/**
 * The walk-me modal's panel order as it existed BEFORE issue #519 added
 * `about_you`. `deriveInitialCompleted()`'s one-time coarse fallback (for
 * accounts that reached this modal before vengreso/flymsg-be#759 started
 * sending `onboarding_steps_completed`) reads a user's `onboardingv2_step`
 * counter, which was advanced under THIS numbering — reusing the live
 * `WELCOME_MODAL_STEP_ORDER` (about_you now at index 1) here would
 * misattribute `about_you` as complete for anyone who merely progressed past
 * the old panel 1 before this feature ever shipped. Never used for anything
 * else — the modal's actual panel switch is driven by `WELCOME_MODAL_STEP_ORDER`.
 */
const LEGACY_ONBOARDINGV2_STEP_ORDER: readonly OnboardingStepKey[] = [
  "install_extension",
  "add_flycut_signature",
  "test_flycut",
  "add_flyplate",
  "create_persona",
  "linkedin_ai_comment_generator",
  "linkedin_social_post_generator",
];

const SALES_PRO_PLAN_IDS = [
  "sales-pro",
  "sales-pro-yearly",
  "sales-pro-monthly",
  "pro-plan-teams-smb",
  "pro-plan-teams-ent",
];

/**
 * Per-user SharedDataService key for this browser's authoritative completed-
 * step snapshot (see the S-11 QA finding on issue #523's PR). Namespaced by
 * user id so a shared browser switching between accounts can't leak one
 * user's completion state into another's derivation; SharedDataService
 * itself already splits storage between localStorage and sessionStorage for
 * masquerading sessions, so no extra handling is needed here for that.
 */
function completedStepsStorageKey(userId: string): string {
  return `onboarding_completed_steps:${userId}`;
}

/**
 * Issue #537: per-user SharedDataService key for the header-hat re-entry
 * tooltip's dismissal counter — same `{prefix}:{userId}` shape as
 * `completedStepsStorageKey`, for the same masquerading-safe reason (a
 * shared browser switching accounts can't leak/inherit another user's
 * dismissal count).
 */
function reentryTooltipDismissalsStorageKey(userId: string): string {
  return `onboarding_hat_tooltip_dismissals:${userId}`;
}

/**
 * Single source of truth for onboarding checklist completion, replacing the
 * monotonic `onboardingv2_step >= N` comparisons that used to live 3x-copied
 * across WelcomeModalComponent, TopSearchBarComponent and
 * GlobalOverlayComponent (issue #523).
 *
 * Two concepts are kept deliberately separate:
 *  - `completed` (this service): REAL per-step completion — drives checkmarks
 *    and the 0-100 percentage. Only advances via `completeStep()`.
 *  - `onboardingv2_step` (still read directly off `user_details` by callers):
 *    the walk-me-modal's NAVIGATION position — which panel is reachable/next.
 *    It keeps advancing via both `completeStep()` and `goToStep()` (skip),
 *    exactly as before, so gating which row is clickable is unaffected by
 *    this change.
 *
 * Rollout compat (flymsg-be#759): today's production backend has not shipped
 * the new `onboarding_steps_completed` field on every response, so this
 * service ALSO keeps its own per-user local-storage snapshot of the
 * completed set as a second, independent source of truth.
 * `deriveInitialCompleted()`'s order is: the UNION of the server field (off
 * the cached `user_details` blob) and this browser's local snapshot, if
 * EITHER is present -> the coarse legacy `onboardingv2_step` derivation, used
 * ONLY when neither exists yet. The union (not "server wins" or "local
 * wins") is deliberate (QA F-9 on PR #533): the cached `user_details` blob is
 * never refreshed after `completeStep()` resolves, so right after an
 * optimistic completion on THIS device the local snapshot is legitimately
 * ahead of it; a completion made on ANOTHER device is legitimately ahead of
 * this browser's local snapshot until it reaches here. Taking either field
 * alone risks silently dropping a step the other side already knows about —
 * exactly what F-9 observed (a freshly-completed step un-ticking on reload).
 * The union is re-persisted locally so a cross-device catch-up sticks here
 * too. The legacy branch immediately persists its own result as the local
 * snapshot before returning it, which is what makes it genuinely one-time:
 * every SUBSEQUENT load (this account's 2nd+ run on this browser) reads the
 * now-existing snapshot instead of re-deriving. Without that write, the
 * legacy branch would re-run on every reload for an account that has never
 * called `completeStep()` yet (e.g. seeded directly at a non-zero
 * `onboardingv2_step`), and since `onboardingv2_step` also advances on skips,
 * every skip would keep getting re-derived as complete (S-11). `goToStep()`
 * (skip) never writes to either completion store, so it can never itself
 * trigger a snapshot write.
 */
@Injectable({ providedIn: "root" })
export class OnboardingProgressService {
  private readonly completedSubject: BehaviorSubject<Set<OnboardingStepKey>>;
  readonly completed$: Observable<ReadonlySet<OnboardingStepKey>>;
  readonly percentage$: Observable<number>;
  /**
   * Issue #520: emits whenever `requiredComplete` (below) flips, so anything
   * gating dismissal (welcome-modal's backdrop/ESC via DialogService) can
   * react WITHOUT the modal being reopened. `distinctUntilChanged` keeps this
   * from re-firing on every unrelated `completeStep()` once both required
   * steps are already done (or not yet touched).
   */
  readonly requiredComplete$: Observable<boolean>;
  /**
   * Issue #537: emits whenever `firstThreeComplete` (below) flips, so the
   * header hat's glow/re-entry-tooltip gating updates live — e.g.
   * completing the 3rd required step while the header is mounted drops the
   * glow without a reload. `distinctUntilChanged` mirrors `requiredComplete$`.
   */
  readonly firstThreeComplete$: Observable<boolean>;

  constructor(
    private readonly authService: AuthService,
    private readonly sharedData: SharedDataService
  ) {
    this.completedSubject = new BehaviorSubject<Set<OnboardingStepKey>>(this.deriveInitialCompleted());
    this.completed$ = this.completedSubject.asObservable();
    this.percentage$ = this.completed$.pipe(map((completed) => this.computePercentage(completed)));
    this.requiredComplete$ = this.completed$.pipe(
      map((completed) => this.computeRequiredComplete(completed)),
      distinctUntilChanged()
    );
    this.firstThreeComplete$ = this.completed$.pipe(
      map((completed) => this.computeFirstThreeComplete(completed)),
      distinctUntilChanged()
    );
  }

  get completed(): ReadonlySet<OnboardingStepKey> {
    return this.completedSubject.value;
  }

  get percentage(): number {
    return this.computePercentage(this.completedSubject.value);
  }

  /**
   * Single source of truth for "the mandatory onboarding steps are done" —
   * issue #520. The welcome-modal's close (X) button and its backdrop/ESC
   * dismissal both read this (directly or via `requiredComplete$`) rather
   * than re-deriving it, so the two can never drift out of sync.
   */
  get requiredComplete(): boolean {
    return this.computeRequiredComplete(this.completedSubject.value);
  }

  /**
   * Issue #537: single source of truth for "the header hat should still
   * glow" — NOT all of FIRST_THREE_ONBOARDING_STEPS are complete yet.
   * Replaces the legacy `!onboardingv2_completed` gate TopSearchBarComponent
   * used to read directly.
   */
  get firstThreeComplete(): boolean {
    return this.computeFirstThreeComplete(this.completedSubject.value);
  }

  /**
   * Issue #537: how many times this user has explicitly dismissed the
   * header-hat re-entry tooltip on this browser. 0 for a user with no
   * stored count yet; a corrupted/non-numeric stored value is treated the
   * same defensive way `readLocalSnapshot` treats a non-array — better to
   * show the tooltip again than to throw on it.
   */
  getReentryTooltipDismissalCount(): number {
    const user = this.authService.userDetails?.user_details;
    if (!user?.id) {
      return 0;
    }
    const stored = this.sharedData.getAttribute<unknown>(reentryTooltipDismissalsStorageKey(user.id));
    return typeof stored === "number" && Number.isFinite(stored) && stored > 0 ? stored : 0;
  }

  /**
   * Issue #537: call ONLY on an explicit dismissal (the tooltip's "Got it!"
   * end button) — never on a programmatic teardown, e.g. the walk-me modal
   * opening while the tooltip happens to be showing. See
   * TopSearchBarComponent.teardownOnboardingReentryTooltip().
   */
  recordReentryTooltipDismissal(): void {
    const user = this.authService.userDetails?.user_details;
    if (!user?.id) {
      return;
    }
    this.sharedData.setAttribute(reentryTooltipDismissalsStorageKey(user.id), this.getReentryTooltipDismissalCount() + 1);
  }

  /**
   * Issue #537: single gate the re-entry tooltip's trigger reads — never
   * shown once the first three steps are complete OR the dismissal counter
   * reaches REENTRY_TOOLTIP_MAX_DISMISSALS, whichever comes first.
   * Deliberately does NOT check whether the walk-me modal is open — that's
   * a GlobalHookService.onboardingActive concern local to
   * TopSearchBarComponent, kept out of this service to avoid pulling that
   * dependency in here.
   *
   * Requires a resolvable user id: without one, `getReentryTooltipDismissalCount()`
   * can't persist a dismissal (it silently no-ops — see above), which would
   * otherwise make the tooltip re-show on every load with no way to
   * suppress it. Safer to not show it at all than to show an unsuppressable one.
   */
  get shouldShowReentryTooltip(): boolean {
    if (!this.authService.userDetails?.user_details?.id) {
      return false;
    }
    return !this.firstThreeComplete && this.getReentryTooltipDismissalCount() < REENTRY_TOOLTIP_MAX_DISMISSALS;
  }

  /**
   * Centralizes the maxStep/showFlyLearning getters previously copy-pasted 3x.
   * Issue #519: derived from WELCOME_MODAL_STEP_ORDER.length (now 8, with
   * `about_you` inserted) instead of a hardcoded literal, so this can't drift
   * out of sync with the panel switch again.
   */
  get maxStep(): number {
    return this.showFlyLearning ? WELCOME_MODAL_STEP_ORDER.length : WELCOME_MODAL_STEP_ORDER.length - 1;
  }

  get showFlyLearning(): boolean {
    const planIdType = this.sharedData.getAttribute("planIdType") ?? this.sharedData.getAttribute("planId");
    return !!planIdType && SALES_PRO_PLAN_IDS.includes(planIdType);
  }

  isCompleted(key: OnboardingStepKey): boolean {
    return this.completedSubject.value.has(key);
  }

  /**
   * A real completion event (video watched to 100%, action actually taken —
   * never a skip). Optimistically updates local state so the header/checklist
   * reflect it immediately, persists that snapshot to this browser's local
   * store (S-11: the second-line source of truth `deriveInitialCompleted()`
   * reads on reload, since today's backend doesn't send
   * `onboarding_steps_completed`), then persists via the extended
   * `update-onboarding` endpoint. `legacyStep`/`legacyMaxStepExclusive` keep
   * feeding the pre-existing `onboardingv2_step` position field so navigation
   * gating (which rows are reachable) is unaffected.
   */
  completeStep(key: OnboardingStepKey, legacyStep: number, legacyMaxStepExclusive: number): Observable<unknown> {
    const user = this.authService.userDetails?.user_details;
    if (!user) {
      return of(null);
    }

    const next = new Set(this.completedSubject.value);
    next.add(key);
    this.completedSubject.next(next);
    this.persistLocally(user.id, next);

    return this.authService
      .patchOnboarding(user.id, legacyStep, legacyMaxStepExclusive, undefined, false, undefined, key)
      .pipe(
        tap((response: unknown) => this.reconcileFromServer(response, user.id)),
        catchError(() => of(null))
      );
  }

  /**
   * Navigation only — advances the legacy position (so the next/skipped panel
   * is reachable and the user's place is remembered) WITHOUT marking the
   * current step complete. This is the fix for "Skip marks the row done":
   * skip buttons call this, never `completeStep`. Deliberately never touches
   * EITHER completion store (server `completed_step` field or the local
   * snapshot) — that's what keeps a skipped row from coming back checked
   * after a reload (S-11).
   */
  goToStep(legacyStep: number, legacyMaxStepExclusive: number): Observable<unknown> {
    const user = this.authService.userDetails?.user_details;
    if (!user) {
      return of(null);
    }
    return this.authService.patchOnboarding(user.id, legacyStep, legacyMaxStepExclusive).pipe(catchError(() => of(null)));
  }

  private reconcileFromServer(response: unknown, userId: string): void {
    const result = ((response as any)?.result ?? response) as { onboarding_steps_completed?: unknown } | undefined;
    const stepsFromServer = result?.onboarding_steps_completed;
    if (!Array.isArray(stepsFromServer)) {
      // flymsg-be#759 not deployed yet (or this call hit an older instance
      // during a rollout) — keep the optimistic local state (already
      // persisted by completeStep()) as-is.
      return;
    }
    const known = stepsFromServer.filter((step): step is OnboardingStepKey =>
      (ONBOARDING_TRACKED_STEPS as readonly string[]).includes(step)
    );
    const set = new Set(known);
    this.completedSubject.next(set);
    this.persistLocally(userId, set);
  }

  /** Writes this browser's authoritative completed-step snapshot. Called from completeStep()/reconcileFromServer() and once from deriveInitialCompleted()'s legacy branch (to make that derivation one-time) — never from goToStep() (S-11). */
  private persistLocally(userId: string, completed: ReadonlySet<OnboardingStepKey>): void {
    this.sharedData.setAttribute(completedStepsStorageKey(userId), Array.from(completed));
  }

  /** Reads this browser's local snapshot. Returns null (not []) when nothing was ever persisted, so callers can distinguish "no snapshot yet" from "snapshot says zero completed". */
  private readLocalSnapshot(userId: string): OnboardingStepKey[] | null {
    const stored = this.sharedData.getAttribute<unknown>(completedStepsStorageKey(userId));
    if (!Array.isArray(stored)) {
      return null;
    }
    return stored.filter((step): step is OnboardingStepKey => (ONBOARDING_TRACKED_STEPS as readonly string[]).includes(step));
  }

  private computePercentage(completed: ReadonlySet<OnboardingStepKey>): number {
    return Math.round((completed.size / ONBOARDING_TRACKED_STEPS.length) * 100);
  }

  private computeRequiredComplete(completed: ReadonlySet<OnboardingStepKey>): boolean {
    return REQUIRED_ONBOARDING_STEPS.every((key) => completed.has(key));
  }

  private computeFirstThreeComplete(completed: ReadonlySet<OnboardingStepKey>): boolean {
    return FIRST_THREE_ONBOARDING_STEPS.every((key) => completed.has(key));
  }

  /** Filters a raw value down to known step keys, or null if it isn't an array at all (as opposed to a real, possibly-empty, completed set). */
  private parseKnownSteps(value: unknown): OnboardingStepKey[] | null {
    if (!Array.isArray(value)) {
      return null;
    }
    return value.filter((step): step is OnboardingStepKey => (ONBOARDING_TRACKED_STEPS as readonly string[]).includes(step));
  }

  /**
   * QA F-9 (round 3 on PR #533): `user.onboarding_steps_completed` comes off
   * the CACHED user-details blob (`authService.userDetails`), which nothing
   * refreshes after `completeStep()` resolves — reconcileFromServer() only
   * updates this service's own in-memory `completedSubject` and the local
   * snapshot, never that cache. The old code took the server field VERBATIM
   * whenever it was present at all (even an empty array), so a page reload
   * seconds after completing a step re-derived from the stale pre-completion
   * cache and un-ticked a row that both the real server AND this browser's
   * own local snapshot already agreed was done.
   *
   * Neither side is reliably "more current" than the other: the local
   * snapshot is legitimately ahead right after an optimistic completeStep()
   * on THIS device (this bug); the server is legitimately ahead right after
   * a completion made on ANOTHER device, before it ever reaches this
   * browser's local snapshot. The safe answer is their UNION — never drop a
   * step either source already knows about — re-persisted locally so a
   * cross-device catch-up sticks here too, exactly like a real completeStep()
   * would leave it.
   */
  private deriveInitialCompleted(): Set<OnboardingStepKey> {
    const user = this.authService.userDetails?.user_details as
      | { id?: string; onboarding_steps_completed?: unknown; onboardingv2_step?: number }
      | undefined;
    if (!user) {
      return new Set();
    }

    const fromServer = this.parseKnownSteps(user.onboarding_steps_completed);
    // S-11: today's production backend may never send onboarding_steps_completed
    // (flymsg-be#759 rollout), so this browser's own local snapshot — written
    // on every real completeStep()/reconciliation, NEVER on a skip — is the
    // next-best source of truth, and it's EXACT (unlike re-deriving from
    // onboardingv2_step, which also advances on skips and would re-mark
    // skipped rows complete on every reload).
    const fromLocal = user.id ? this.readLocalSnapshot(user.id) : null;

    if (fromServer !== null || fromLocal !== null) {
      const union = new Set<OnboardingStepKey>([...(fromServer ?? []), ...(fromLocal ?? [])]);
      if (user.id) {
        this.persistLocally(user.id, union);
      }
      return union;
    }

    // Last resort: no server field, no local snapshot yet (this account's
    // first run on this browser since #523 shipped) — derive a one-time,
    // necessarily coarse guess from the legacy counter, then IMMEDIATELY
    // persist it as the local snapshot. That write is what makes this
    // genuinely one-time: without it, this branch re-runs on every reload
    // (not just the first), re-deriving from whatever onboardingv2_step has
    // grown to since — including further skips — which reproduces S-11 for
    // any account that reaches this branch with zero real completions yet
    // (e.g. a QA/support fixture seeded directly at a non-zero step). Once
    // persisted, the next load reads the snapshot (the branch above) instead
    // of re-deriving, and every subsequent completeStep() only adds to it.
    const legacyStep = Number(user.onboardingv2_step) || 0;
    const derived = new Set<OnboardingStepKey>();
    for (let i = 0; i < Math.min(legacyStep, LEGACY_ONBOARDINGV2_STEP_ORDER.length); i++) {
      derived.add(LEGACY_ONBOARDINGV2_STEP_ORDER[i]);
    }
    if (user.id) {
      this.persistLocally(user.id, derived);
    }
    return derived;
  }
}
